import {
  generateIndirectTaxFilingV2Calendar,
  IIndirectTaxFilingV2,
  IIndirectTaxFilingV2CalendarPreview,
  SalesTaxFilingFrequencyEnum,
  useIndirectTaxFilingV2CalendarPreview,
} from '@commenda/api/indirect-tax/filings';
import { useIndirectTaxAvailableRegistrations } from '@commenda/api/indirect-tax/registrations';
import { IIndirectTaxRegistrationV2Response } from '@commenda/api/indirect-tax/registrations/types';
import { Alert, Button, Icons } from '@commenda/kakashi';
import { useEffect, useMemo, useState } from 'react';

import { useNotification } from '../../../../../hooks/useNotification';
import { VALIDATION_MESSAGES } from '../../constants';
import {
  CalendarPreviewValidationError,
  FilingCalendarPreviewData,
  FilingPreviewEntry,
  MonthYearValue,
} from '../../types';
import { CalendarPreviewHelper } from '../../helpers';
import { IndirectTaxFilingFrequencyCombinationSelector } from '../inputs/IndirectTaxFilingFrequencyCombinationSelector';
import { IndirectTaxRegistrationSelector } from '../inputs/IndirectTaxRegistrationSelector';
import { TaxPeriodMonthPicker } from '../inputs/TaxPeriodMonthPicker';
import { CalendarGenerationConfirmationModal } from './CalendarGenerationConfirmationModal';

interface FilingCalendarPreviewFormProps {
  corporationId: number;
  initialTaxPeriodStart?: MonthYearValue;
  onSuccess?: () => void;
  onPreviewDataChange?: (data: FilingCalendarPreviewData | undefined) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  onTaxPeriodChange?: (
    start: MonthYearValue | null,
    end: MonthYearValue | null
  ) => void;
  onPreviewGenerated?: (generated: boolean) => void;
  /** Register a no-arg callback to refetch the preview (e.g. after deleting a filing). */
  registerRefetchPreview?: (fn: () => void) => void;
}

const FILING_STATUS_FALLBACK = 'SCHEDULED';

// Converts a single V2 filing into the snake_case entry shape the existing
// preview content / cards / table were built against.
function toPreviewEntry(filing: IIndirectTaxFilingV2): FilingPreviewEntry {
  return {
    id: filing.filingId ?? filing.id,
    form_title: filing.formTitle,
    state: filing.jurisdictionName ?? '',
    filing_frequency: filing.filingFrequency,
    tax_period_start_date: filing.taxPeriodStartDate,
    tax_period_end_date: filing.taxPeriodEndDate,
    reporting_period_start_date: filing.reportingPeriodStartDate,
    reporting_period_end_date: filing.reportingPeriodEndDate,
    due_date: filing.filingDueDate,
    // filing.status comes from the V2 API as a string enum; the shared
    // FilingPreviewEntry type was minted against the legacy API union. They
    // share the same wire values, so the narrowing is safe.
    status: (filing.status ?? FILING_STATUS_FALLBACK) as FilingPreviewEntry['status'],
    description: filing.description ?? undefined,
  };
}

function toPreviewData(
  preview: IIndirectTaxFilingV2CalendarPreview | undefined
): FilingCalendarPreviewData | undefined {
  if (!preview) return undefined;
  return {
    existing_filings: preview.existingFilings.map(toPreviewEntry),
    proposed_filings: preview.proposedFilings.map(toPreviewEntry),
    filings_to_delete: preview.filingsToDelete.map(toPreviewEntry),
    final_calendar: preview.finalCalendar.map(toPreviewEntry),
    has_filed_conflict: preview.hasFiledConflict,
    conflicting_filings: preview.conflictingFilings?.map(toPreviewEntry),
    has_coverage_gaps: preview.hasCoverageGaps,
    coverage_gaps: preview.coverageGaps?.map((gap) => ({
      tax_period_start_date: gap.taxPeriodStartDate,
      tax_period_end_date: gap.taxPeriodEndDate,
      previously_covered_by: gap.previouslyCoveredBy,
      reason: gap.reason,
    })),
    // validationErrors is `string[] | undefined` on the V2 response; the
    // legacy shape is a plain string[]. Widen with a default; the value is
    // never null here.
    validation_errors:
      (preview.validationErrors ?? []) as FilingCalendarPreviewData['validation_errors'],
    can_generate: preview.canGenerate,
  };
}

export function FilingCalendarPreviewForm({
  corporationId,
  initialTaxPeriodStart,
  onSuccess,
  onPreviewDataChange,
  onLoadingChange,
  onTaxPeriodChange,
  onPreviewGenerated,
  registerRefetchPreview,
}: FilingCalendarPreviewFormProps) {
  const { setNotification } = useNotification();

  const [registrationTaxTypeId, setRegistrationTaxTypeId] = useState<string>('');
  const [selectedRegistration, setSelectedRegistration] =
    useState<IIndirectTaxRegistrationV2Response | null>(null);
  const [frequencyComboId, setFrequencyComboId] = useState<string>('');
  const [selectedFrequencies, setSelectedFrequencies] = useState<
    SalesTaxFilingFrequencyEnum[]
  >([]);
  const [taxPeriodStart, setTaxPeriodStart] = useState<MonthYearValue | null>(
    initialTaxPeriodStart ?? null
  );
  const [taxPeriodEnd, setTaxPeriodEnd] = useState<MonthYearValue | null>(null);
  const [validationErrors] = useState<CalendarPreviewValidationError[]>([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    onTaxPeriodChange?.(taxPeriodStart, taxPeriodEnd);
  }, [taxPeriodStart, taxPeriodEnd, onTaxPeriodChange]);

  useEffect(() => {
    if (initialTaxPeriodStart !== undefined) {
      setTaxPeriodStart(initialTaxPeriodStart);
    }
  }, [initialTaxPeriodStart]);

  // Content-driven list of frequency options for this registration. The
  // available-registrations endpoint returns frequencies pre-structured as
  // { primary, related? } entries — no client-side combination logic.
  const { data: availableRegistrations } = useIndirectTaxAvailableRegistrations(
    selectedRegistration?.jurisdictionId
      ? {
          corporationId,
          jurisdictionIds: [selectedRegistration.jurisdictionId],
        }
      : null
  );

  const frequencyOptions = useMemo(() => {
    if (!selectedRegistration || !availableRegistrations) return [];
    const match = availableRegistrations
      .flatMap((r) => r.taxTypeOptions)
      .find(
        (opt) =>
          opt.registrationTaxTypeId === selectedRegistration.registrationTaxTypeId
      );
    return match?.frequencies ?? [];
  }, [selectedRegistration, availableRegistrations]);

  const previewReady = !!registrationTaxTypeId && !!taxPeriodStart;

  const {
    preview: v2Preview,
    isLoading: isPreviewLoading,
    mutate: mutatePreview,
  } = useIndirectTaxFilingV2CalendarPreview(
    previewReady && taxPeriodStart
      ? {
          corporationId,
          registrationTaxTypeId,
          filingFrequencies:
            selectedFrequencies.length > 0 ? selectedFrequencies : undefined,
          taxPeriodStartMonth: CalendarPreviewHelper.monthYearToYYYYMM(taxPeriodStart),
          taxPeriodEndMonth: taxPeriodEnd
            ? CalendarPreviewHelper.monthYearToYYYYMM(taxPeriodEnd)
            : undefined,
        }
      : null
  );

  const previewData = useMemo(() => toPreviewData(v2Preview), [v2Preview]);

  useEffect(() => {
    onPreviewDataChange?.(previewData);
  }, [previewData, onPreviewDataChange]);

  useEffect(() => {
    onLoadingChange?.(isPreviewLoading);
  }, [isPreviewLoading, onLoadingChange]);

  useEffect(() => {
    registerRefetchPreview?.(() => mutatePreview?.());
  }, [registerRefetchPreview, mutatePreview]);

  useEffect(() => {
    if (previewData) {
      onPreviewGenerated?.(previewData.final_calendar.length > 0);
    }
  }, [previewData, onPreviewGenerated]);

  const handleGenerate = async () => {
    if (!previewData?.can_generate) return;
    if (!registrationTaxTypeId || !taxPeriodStart) return;

    setIsGenerating(true);
    const result = await generateIndirectTaxFilingV2Calendar({
      params: {
        corporationId,
        registrationTaxTypeId,
        filingFrequencies:
          selectedFrequencies.length > 0 ? selectedFrequencies : undefined,
        taxPeriodStartMonth: CalendarPreviewHelper.monthYearToYYYYMM(taxPeriodStart),
        taxPeriodEndMonth: taxPeriodEnd
          ? CalendarPreviewHelper.monthYearToYYYYMM(taxPeriodEnd)
          : undefined,
      },
    });
    setIsGenerating(false);

    if (result.success) {
      setShowConfirmationModal(false);
      onSuccess?.();
      setNotification({
        title: 'Success',
        message: 'Filing calendar generated successfully',
        show: true,
        valid: true,
      });
    } else {
      setNotification({
        title: 'Failed to generate',
        message: result.error ?? 'Could not generate filing calendar',
        show: true,
        valid: false,
      });
    }
  };

  const handleConfirmClick = () => {
    if (!previewData?.can_generate) return;
    setShowConfirmationModal(true);
  };

  const getFieldError = (field: string) =>
    validationErrors.find((e) => e.field === field)?.message;

  const generalError = validationErrors.find((e) => e.field === 'general');
  const apiValidationErrors = previewData?.validation_errors ?? [];
  const hasApiValidationErrors = apiValidationErrors.length > 0;
  const canConfirm = !!previewData?.can_generate && !isGenerating;

  const totalGapMonths = useMemo(() => {
    if (!previewData?.coverage_gaps || previewData.coverage_gaps.length === 0) {
      return 0;
    }
    return previewData.coverage_gaps.reduce((total, gap) => {
      const start = CalendarPreviewHelper.isoStringToMonthYear(gap.tax_period_start_date);
      const end = CalendarPreviewHelper.isoStringToMonthYear(gap.tax_period_end_date);
      const monthsDiff = CalendarPreviewHelper.getMonthsDifference(start, end);
      return total + monthsDiff;
    }, 0);
  }, [previewData?.coverage_gaps]);

  return (
    <>
      <div className="flex flex-col gap-3xl">
        <IndirectTaxRegistrationSelector
          corporationId={corporationId}
          value={registrationTaxTypeId}
          onChange={(id, registration) => {
            setRegistrationTaxTypeId(id);
            setSelectedRegistration(registration);
            // Reset frequency whenever the registration changes — the
            // available options depend on the registration's own frequencies.
            setFrequencyComboId('');
            setSelectedFrequencies([]);
          }}
          disabled={isPreviewLoading}
          error={getFieldError('registrationTaxTypeId')}
        />

        <IndirectTaxFilingFrequencyCombinationSelector
          frequencyOptions={frequencyOptions}
          value={frequencyComboId}
          onChange={(id, frequencies) => {
            setFrequencyComboId(id);
            setSelectedFrequencies(frequencies);
          }}
          disabled={isPreviewLoading || !registrationTaxTypeId}
          error={getFieldError('filingFrequency')}
        />

        <TaxPeriodMonthPicker
          label="Tax period start"
          value={taxPeriodStart}
          onChange={setTaxPeriodStart}
          placeholder="Select month"
          disabled={isPreviewLoading}
          error={getFieldError('taxPeriodStart')}
        />

        <TaxPeriodMonthPicker
          label="Tax period end"
          value={taxPeriodEnd}
          onChange={setTaxPeriodEnd}
          placeholder="MM / YYYY"
          minDate={taxPeriodStart ?? undefined}
          disabled={isPreviewLoading}
          error={getFieldError('taxPeriodEnd')}
          isOptional
        />

        <div className="flex gap-md">
          <Button
            variant="primary"
            onClick={handleConfirmClick}
            disabled={!canConfirm}
            className="flex-1"
          >
            {isGenerating ? 'Generating...' : 'Confirm'}
          </Button>
        </div>

        <div className="flex flex-col gap-md">
          {previewData?.has_coverage_gaps && previewData.coverage_gaps ? (
            <Alert
              variant="warning"
              icon={<Icons.AlertCircle className="size-5 text-text-warning" />}
              title="Skipped filing(s)"
              description={
                totalGapMonths === 1
                  ? `${totalGapMonths} tax period is not covered by any filing. Adjust the start or end month to cover them.`
                  : `${totalGapMonths} tax periods are not covered by any filing. Adjust the start or end month to cover them.`
              }
            />
          ) : previewData?.has_filed_conflict ? (
            <Alert
              variant="destructive"
              icon={<Icons.AlertCircle className="size-5 text-text-error" />}
              title="Cannot generate calendar"
              description={VALIDATION_MESSAGES.FILED_CONFLICT}
            />
          ) : generalError ? (
            <Alert
              variant="destructive"
              icon={<Icons.AlertCircle className="size-5 text-text-error" />}
              title={generalError.message}
            />
          ) : hasApiValidationErrors ? (
            <Alert
              variant="destructive"
              icon={<Icons.AlertCircle className="size-5 text-text-error" />}
              title="Cannot generate calendar"
              description={
                apiValidationErrors.length === 1 ? (
                  apiValidationErrors[0]
                ) : (
                  <ul className="list-disc list-inside mt-xs">
                    {apiValidationErrors.map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                )
              }
            />
          ) : null}
        </div>
      </div>

      <CalendarGenerationConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={handleGenerate}
        previewData={previewData}
        isGenerating={isGenerating}
      />
    </>
  );
}
